import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { PROJECTS, RECOG, META } from './data';

// HOME — blueprint grid. The hero IS the grid (12 cols visible). Text aligns to it.
// Distinctive: visible measurement marks at the top edge.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#DADCD6] text-[#1B1A18]">
      {/* Measurement strip across top */}
      <div className="border-b border-[#1E3A5F]/15">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-8 flex items-center font-mono text-[8px] tracking-[0.3em] uppercase text-[#1E3A5F]/55 gap-2">
          {[...Array(13)].map((_, i) => <span key={i} className="flex items-center gap-2 flex-1"><span className="w-px h-2 bg-[#1E3A5F]/35" /><span className="hidden md:inline">{i * 100}</span></span>)}
        </div>
      </div>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-12 gap-4 lg:gap-6">
        {/* Title spans 8 cols */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="col-span-12 lg:col-span-8">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#1E3A5F]">/ Studio · est. {META.est} · London + Lugano</p>
          <h1 className="font-serif text-[44px] sm:text-[72px] lg:text-[120px] leading-[0.92] tracking-[-0.025em] mt-6 text-[#1E3A5F]">
            A studio that <span className="italic block">draws everything</span>.
          </h1>
          <p className="text-base lg:text-lg text-[#1B1A18]/75 leading-[1.7] mt-8 max-w-2xl">We design buildings, interiors, and the joinery that connects them. We do not divide the work between disciplines because, finally, no one we have ever built for has either.</p>
        </motion.div>
        {/* Right column: project ticker */}
        <aside className="col-span-12 lg:col-span-4 lg:border-l border-[#1E3A5F]/20 lg:pl-6">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#1E3A5F] border-b border-[#1E3A5F]/20 pb-3 mb-3">/ In progress</p>
          <ul className="space-y-3 font-mono text-[11px]">
            {PROJECTS.map(p => (
              <li key={p.code} className="grid grid-cols-[60px_1fr_auto] gap-3 py-1.5 border-b border-[#1E3A5F]/10">
                <span className="text-[#1E3A5F]/55">{p.code}</span>
                <span className="text-[#1B1A18]" dangerouslySetInnerHTML={{ __html: p.t }} />
                <span className="text-[#E5C61F] bg-[#1E3A5F] px-1.5 py-0.5 text-[9px] tracking-[0.2em] uppercase">{p.yr}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* Hero project (grid spans 12 cols, image cropped to blueprint ratio) */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.3 }} className="col-span-12 mt-8 lg:mt-16 relative">
          <div className="aspect-[16/8] overflow-hidden bg-[#1E3A5F]/10 border border-[#1E3A5F]/20">
            <img src={PROJECTS[1].img} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-4 left-4 right-4 flex justify-between font-mono text-[9px] tracking-[0.3em] uppercase text-white">
            <span className="bg-[#1E3A5F] px-2 py-1">/ Featured · {PROJECTS[1].code}</span>
            <span className="bg-[#E5C61F] text-[#1E3A5F] px-2 py-1">{PROJECTS[1].sqm} m²</span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1E3A5F]/95 to-[#1E3A5F]/0 p-6 lg:p-10">
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#E5C61F] mb-2">{PROJECTS[1].cat} · {PROJECTS[1].loc}</p>
            <h2 className="font-serif text-3xl lg:text-5xl text-white leading-tight">{PROJECTS[1].t}</h2>
            <p className="font-serif italic text-base text-white/75 mt-2 max-w-2xl" dangerouslySetInnerHTML={{ __html: PROJECTS[1].brief }} />
          </div>
        </motion.div>

        {/* Stats row at the bottom of the grid */}
        <div className="col-span-12 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-12 border-t-2 border-[#1E3A5F] pt-8">
          {[['38', 'Built projects, since 2003'], ['8', 'Average projects in any year'], ['26', 'Studio personnel'], ['11', 'Awards, including AIA Honor']].map(([v, l]) => (
            <div key={l} className="border-l-2 border-[#E5C61F] pl-4">
              <p className="font-serif text-5xl lg:text-7xl text-[#1E3A5F] leading-none">{v}</p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B1A18]/65 mt-3">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Recognition ledger */}
      <section className="bg-[#1E3A5F] text-[#DADCD6] py-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E5C61F] mb-4">/ Recognition · selected</p>
          <div className="border-t-2 border-[#E5C61F]">
            {RECOG.map(r => (
              <div key={r.t} className="grid grid-cols-[80px_1fr_auto] gap-4 py-6 border-b border-white/10 items-baseline">
                <span className="font-serif text-2xl text-[#E5C61F]">{r.yr}</span>
                <span className="font-serif text-xl lg:text-2xl">{r.t}</span>
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">External</span>
              </div>
            ))}
          </div>
          <button onClick={() => setPage('projects')} className="mt-10 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#E5C61F] hover:text-white">
            All thirty-eight projects <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      </section>
    </div>
  );
}
